import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ProfileData } from 'hooks/useAuth';
import { useResponsiveScreenSizes } from 'hooks/useResponsiveScreenSizes';

import {
    checkImageUrl,
    formatInstagramProfileUrl,
    formatLinkedInUrl,
} from 'libs/utils';

import { PursuitProfileRawData } from 'components/layout/ProfileContainer';
import { Avatar } from 'components/ui/Avatar';
import { Button } from 'components/ui/Button';

import { LinkedIn } from 'components/svg/LinkedIn';
import { Pencil } from 'components/svg/Pencil';

import InstagramLogo from 'assets/instagram-logo.png';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, HorizontalFlexWrapper, NestedCard } from 'styles/shared';

interface ProfileDetailsProps {
    profile: ProfileData;
    isCurrentUser: boolean;
    onEdit?: () => void;
}

export const ProfileDetails = ({
    profile,
    isCurrentUser,
    onEdit,
}: ProfileDetailsProps) => {
    const { isMobileScreenSize } = useResponsiveScreenSizes();

    const [isImageValid, setIsImageValid] = useState<boolean>(false);

    const rawData = profile.raw_data as PursuitProfileRawData | null;

    useEffect(() => {
        const validateImage = async () => {
            if (rawData?.profile_image_url) {
                const valid = await checkImageUrl(rawData.profile_image_url);
                setIsImageValid(valid);
            } else {
                setIsImageValid(false);
            }
        };
        validateImage();
    }, [rawData?.profile_image_url]);

    return (
        <>
            {rawData?.profile_image_url == null ||
            rawData?.profile_image_url === '' ||
            !isImageValid ? null : (
                <div style={{ marginTop: '16px', marginLeft: '16px' }}>
                    <Avatar
                        fullName={profile.full_name || ''}
                        imageUrl={rawData.profile_image_url}
                        size="xLarge"
                    />
                    <Bumper />
                </div>
            )}
            {isMobileScreenSize ? (
                <Fonts.Heading6>{profile.full_name}</Fonts.Heading6>
            ) : (
                <Fonts.Heading5>{profile.full_name}</Fonts.Heading5>
            )}
            <Bumper $minHeight={spacing.xxSmall} />
            <Fonts.Large>{rawData?.headline}</Fonts.Large>
            <Bumper $minHeight={spacing.xxxSmall} />
            <Fonts.Medium>{rawData?.company}</Fonts.Medium>
            <Bumper $minHeight={spacing.xxxSmall} />
            <Fonts.Medium $color={colors.grey30}>
                {rawData?.location}
            </Fonts.Medium>
            <Bumper $minHeight={spacing.small} />
            <HorizontalFlexWrapper>
                <Link
                    to={formatLinkedInUrl(profile.linkedin ?? '')}
                    target="_blank"
                    rel="noreferrer"
                >
                    <Button $style="ghost" $isSmall>
                        <HorizontalFlexWrapper>
                            <LinkedIn width={24} />
                            <Bumper $minWidth={spacing.xxSmall} />
                            <Fonts.Small>{`LinkedIn`}</Fonts.Small>
                        </HorizontalFlexWrapper>
                    </Button>
                </Link>

                {rawData?.instagram == null ||
                rawData?.instagram === '' ? null : (
                    <>
                        <Bumper $minWidth={spacing.xSmall} />
                        <Link
                            to={formatInstagramProfileUrl(rawData.instagram)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Button $style="ghost" $isSmall>
                                <HorizontalFlexWrapper>
                                    <img
                                        src={InstagramLogo}
                                        width={24}
                                        alt="instagram-logo"
                                    />
                                    <Bumper $minWidth={spacing.xxSmall} />
                                    <Fonts.Small>{`Instagram`}</Fonts.Small>
                                </HorizontalFlexWrapper>
                            </Button>
                        </Link>
                    </>
                )}
            </HorizontalFlexWrapper>
            <Bumper $minHeight={spacing.medium} />

            <NestedCard>
                <Fonts.Small $isOpenSansSemiBold>
                    {`Zone of Genius`}
                </Fonts.Small>
                <Bumper $minHeight={spacing.xxxSmall} />
                <Fonts.MediumQuote>{rawData?.zone_of_genius}</Fonts.MediumQuote>
                <Bumper />
                <Fonts.Small $isOpenSansSemiBold>
                    {`Passions, hobbies, and interests`}
                </Fonts.Small>
                <Bumper $minHeight={spacing.xxxSmall} />
                <Fonts.MediumQuote>{rawData?.interests}</Fonts.MediumQuote>

                {rawData?.children && (
                    <>
                        <Bumper />
                        <Fonts.Small
                            $isOpenSansSemiBold
                        >{`Children`}</Fonts.Small>
                        <Bumper $minHeight={spacing.xxxSmall} />
                        <Fonts.MediumQuote>
                            {rawData?.children}
                        </Fonts.MediumQuote>
                    </>
                )}

                <Bumper $minHeight={spacing.xxxSmall} />
            </NestedCard>

            {isCurrentUser && onEdit && (
                <>
                    <Bumper />
                    <HorizontalFlexWrapper $isContentSpread>
                        <div />
                        <Button
                            $style="outline"
                            onClick={onEdit}
                            $isStretch={false}
                        >
                            <HorizontalFlexWrapper>
                                <Pencil />
                                <Fonts.Small
                                    style={{ marginLeft: spacing.xSmall }}
                                >
                                    Edit Profile
                                </Fonts.Small>
                            </HorizontalFlexWrapper>
                        </Button>
                    </HorizontalFlexWrapper>
                </>
            )}
        </>
    );
};
